import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  dateOnlyKey,
  depositAndBalance,
  formatCurrency,
  localDayKey,
  scheduleProblem,
  scheduleRemainder,
  splitEvenly,
  toCents,
  type ExpenseItemWithRelations,
  type InstallmentInput,
} from '@peles/shared';
import { useDialog } from '../../../context/DialogContext';
import { useReplaceInstallments } from '../../../hooks/useExpenses';
import { extractApiErrorMessage } from '../../../utils/apiError';

interface Row {
  key: number;
  label: string;
  amount: string;
  dueDate: string;
}

export interface InstallmentEditorProps {
  item: ExpenseItemWithRelations;
  /** The event's day, `YYYY-MM-DD` — where a balance falls by default. */
  eventDay?: string | null;
  /** Called once the schedule is saved (or cleared). */
  onSaved?: () => void;
  onCancel?: () => void;
}

let nextKey = 1;
const toRows = (rows: readonly InstallmentInput[]): Row[] =>
  rows.map((r) => ({
    key: nextKey++,
    label: r.label ?? '',
    amount: String(r.amount),
    dueDate: r.dueDate,
  }));

/** `YYYY-MM-DD` plus whole months, the day held to the month's last. */
export function addMonthsToDay(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
}

/** The rows as the API takes them; an amount that is not a number reads as 0. */
export function readRows(rows: readonly Row[]): InstallmentInput[] {
  return rows.map((r) => ({
    label: r.label.trim() || null,
    amount: Number.isFinite(Number(r.amount)) ? Number(r.amount) : 0,
    dueDate: r.dueDate,
  }));
}

const input =
  'min-h-11 sm:min-h-0 px-2 py-1.5 border border-gray-300 rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const secondary =
  'min-h-11 sm:min-h-8 px-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50';

/**
 * A booked line's payment schedule: presets (a deposit and the balance, or the
 * price split into N monthly payments) or rows typed by hand, with a live
 * remainder so the installments always come to the price — "Balance absorbs
 * it" puts what is left on the last one. Saved whole; empty clears it and the
 * line keeps its single due date.
 */
export function InstallmentEditor({ item, eventDay, onSaved, onCancel }: InstallmentEditorProps) {
  const replace = useReplaceInstallments();
  const { alert, confirm } = useDialog();
  const today = localDayKey();
  const balanceDay = eventDay || (item.dueDate ? dateOnlyKey(item.dueDate) : null) || today;
  const [rows, setRows] = useState<Row[]>(() => toRows(item.installments));
  const [percent, setPercent] = useState('30');
  const [parts, setParts] = useState('3');

  // Another line opened in the same editor starts from its own schedule.
  useEffect(() => {
    setRows(toRows(item.installments));
  }, [item.id]);

  const parsed = useMemo(() => readRows(rows), [rows]);
  const remainder = scheduleRemainder(item.price, parsed);

  const set = (key: number, patch: Partial<Row>) =>
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const applyDeposit = () => {
    const pct = Number(percent);
    if (!(pct > 0 && pct <= 100)) {
      void alert({ title: 'Deposit', message: 'Give the deposit as a percent from 1 to 100.' });
      return;
    }
    setRows(toRows(depositAndBalance(item.price, { percent: pct }, today, balanceDay)));
  };

  const applySplit = () => {
    const n = Number(parts);
    if (!Number.isInteger(n) || n < 2 || n > 24) {
      void alert({ title: 'Split', message: 'Split into 2 to 24 payments.' });
      return;
    }
    const days = Array.from({ length: n }, (_, i) => addMonthsToDay(today, i));
    setRows(toRows(splitEvenly(item.price, days)));
  };

  const absorb = () => {
    if (!rows.length) return;
    const last = rows[rows.length - 1];
    const amount = (toCents(Number(last.amount) || 0) + toCents(remainder)) / 100;
    set(last.key, { amount: String(amount) });
  };

  const save = (next: InstallmentInput[]) =>
    replace.mutate(
      { expenseItemId: item.id, installments: next },
      {
        onSuccess: () => onSaved?.(),
        onError: (err) =>
          void alert({
            title: 'Schedule not saved',
            message: extractApiErrorMessage(err, 'Please try again.'),
            variant: 'error',
          }),
      },
    );

  const onSave = () => {
    const problem = scheduleProblem(item.price, parsed);
    if (problem) {
      void alert({ title: 'Schedule does not add up', message: problem, variant: 'warning' });
      return;
    }
    save(parsed);
  };

  const onClear = async () => {
    if (
      await confirm({
        title: 'Clear the schedule',
        message: 'The line goes back to one due date. Payments already made stay.',
        confirmLabel: 'Clear',
        variant: 'danger',
      })
    ) {
      save([]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Deposit %</span>
          <input
            type="number"
            min="1"
            max="100"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            className={`${input} w-20`}
          />
        </label>
        <button type="button" className={secondary} onClick={applyDeposit}>
          Deposit + balance
        </button>
        <label className="block ml-0 sm:ml-4">
          <span className="block text-xs text-gray-500 mb-1">Payments</span>
          <input
            type="number"
            min="2"
            max="24"
            value={parts}
            onChange={(e) => setParts(e.target.value)}
            className={`${input} w-20`}
          />
        </label>
        <button type="button" className={secondary} onClick={applySplit}>
          Split monthly
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          No schedule — the line is due in one payment
          {item.dueDate ? ` on its due date` : ''}. Pick a preset or add a payment.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row, index) => (
            <li
              key={row.key}
              className="grid grid-cols-1 sm:grid-cols-[1fr_8rem_10rem_auto] gap-2 items-center"
            >
              <input
                aria-label={`Payment ${index + 1} label`}
                value={row.label}
                maxLength={100}
                placeholder={`Payment ${index + 1}`}
                onChange={(e) => set(row.key, { label: e.target.value })}
                className={input}
              />
              <input
                aria-label={`Payment ${index + 1} amount`}
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={row.amount}
                onChange={(e) => set(row.key, { amount: e.target.value })}
                className={input}
              />
              <input
                aria-label={`Payment ${index + 1} due date`}
                type="date"
                value={row.dueDate}
                onChange={(e) => set(row.key, { dueDate: e.target.value })}
                className={input}
              />
              <button
                type="button"
                aria-label={`Remove payment ${index + 1}`}
                onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center text-gray-400 hover:text-red-600 rounded-lg"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button
          type="button"
          className={`${secondary} inline-flex items-center gap-1`}
          onClick={() =>
            setRows((current) => [
              ...current,
              ...toRows([
                {
                  label: '',
                  amount: Math.max(0, remainder),
                  dueDate: current.length ? balanceDay : today,
                },
              ]),
            ])
          }
        >
          <Plus size={14} /> Add a payment
        </button>
        {rows.length > 0 && (
          <p
            role="status"
            className={toCents(remainder) === 0 ? 'text-green-700' : 'text-amber-700'}
          >
            {toCents(remainder) === 0
              ? `Comes to the price, ${formatCurrency(item.price)}`
              : remainder > 0
                ? `${formatCurrency(remainder)} still to schedule`
                : `${formatCurrency(-remainder)} over the price`}
            {toCents(remainder) !== 0 && (
              <button type="button" className="ml-2 underline min-h-11 sm:min-h-0" onClick={absorb}>
                Balance absorbs it
              </button>
            )}
          </p>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2 border-t border-gray-100">
        <div>
          {item.installments.length > 0 && (
            <button
              type="button"
              onClick={() => void onClear()}
              disabled={replace.isPending}
              className="min-h-11 sm:min-h-9 px-3 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Clear schedule
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <button type="button" className={secondary} onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={replace.isPending || rows.length === 0}
            className="min-h-11 sm:min-h-9 px-4 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {replace.isPending ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
